import {
  available,
  checkedAddU128,
  isNonNegativeI64,
  isPositiveU64,
  isU64,
  isU128,
  isU256,
  unavailable,
} from './integer.js';
import type { PoolMathResult } from './types.js';

const PERCENT_POINTS = 100n;
const PNL_PERCENT_INTERMEDIATE_EXCLUSIVE_BOUND = 1n << 199n;

/** One million fixed-point units per percentage point (six decimal places). */
export const PNL_PERCENT_SCALE = 1_000_000n;

export type PnlPercentageRounding = 'toward_zero' | 'nearest_away_from_zero';

export interface PnlPercentageScaledInput {
  pnlD6: bigint;
  costBasisD6: bigint;
  /** Fixed-point units per one percentage point; 1_000_000 gives six decimals. */
  scale: bigint;
  rounding: PnlPercentageRounding;
}

export interface PositionPnlComponentD6 {
  currentValueD6: bigint;
  remainingCostBasisNetD6: bigint;
}

export interface AggregatePositionPnlD6Input {
  realizedPnlD6: bigint;
  positions: readonly PositionPnlComponentD6[];
}

export interface AggregatePositionPnlD6 {
  currentValueD6: bigint;
  remainingCostBasisNetD6: bigint;
  unrealizedPnlD6: bigint;
  totalPnlD6: bigint;
}

/**
 * Combines full-position values with booked realized PnL. Current values are
 * u64 quote outputs, DB cost-basis rows are nonnegative i64, and account-level
 * sums plus signed PnL magnitudes use the checked u128 domain.
 */
export function aggregatePositionPnlD6(
  input: AggregatePositionPnlD6Input,
): PoolMathResult<AggregatePositionPnlD6> {
  if (!isU128(absoluteValue(input.realizedPnlD6))) return unavailable('invalid_amount');

  let currentValueD6 = 0n;
  let remainingCostBasisNetD6 = 0n;
  for (const position of input.positions) {
    if (!isU64(position.currentValueD6) || !isNonNegativeI64(position.remainingCostBasisNetD6)) {
      return unavailable('invalid_amount');
    }
    const nextValue = checkedAddU128(currentValueD6, position.currentValueD6);
    if (!nextValue.available) return nextValue;
    currentValueD6 = nextValue.value;
    const nextBasis = checkedAddU128(remainingCostBasisNetD6, position.remainingCostBasisNetD6);
    if (!nextBasis.available) return nextBasis;
    remainingCostBasisNetD6 = nextBasis.value;
  }

  const unrealizedPnlD6 = currentValueD6 - remainingCostBasisNetD6;
  const totalPnlD6 = input.realizedPnlD6 + unrealizedPnlD6;
  if (!isU128(absoluteValue(unrealizedPnlD6)) || !isU128(absoluteValue(totalPnlD6))) {
    return unavailable('invalid_amount');
  }

  return available({
    currentValueD6,
    remainingCostBasisNetD6,
    unrealizedPnlD6,
    totalPnlD6,
  });
}

/**
 * Returns signed percentage points at the requested integer scale.
 * A zero cost basis has no defined return and is explicitly unavailable.
 */
export function pnlPercentageScaled(input: PnlPercentageScaledInput): PoolMathResult<bigint> {
  if (
    !isU128(absoluteValue(input.pnlD6)) ||
    !isU128(input.costBasisD6) ||
    !isPositiveU64(input.scale)
  ) {
    return unavailable('invalid_amount');
  }
  if (input.costBasisD6 === 0n) return unavailable('zero_cost_basis');

  const numeratorMagnitude = absoluteValue(input.pnlD6) * PERCENT_POINTS * input.scale;
  if (
    !isU256(numeratorMagnitude) ||
    numeratorMagnitude >= PNL_PERCENT_INTERMEDIATE_EXCLUSIVE_BOUND
  ) {
    return unavailable('invalid_amount');
  }
  const numerator = input.pnlD6 < 0n ? -numeratorMagnitude : numeratorMagnitude;
  const scaledPercentage =
    input.rounding === 'toward_zero'
      ? numerator / input.costBasisD6
      : divideNearestAwayFromZero(numerator, input.costBasisD6);
  const scaledMagnitude = absoluteValue(scaledPercentage);
  return isU256(scaledMagnitude) && scaledMagnitude < PNL_PERCENT_INTERMEDIATE_EXCLUSIVE_BOUND
    ? available(scaledPercentage)
    : unavailable('invalid_amount');
}

function divideNearestAwayFromZero(numerator: bigint, denominator: bigint): bigint {
  const magnitude = (absoluteValue(numerator) + denominator / 2n) / denominator;
  return numerator < 0n ? -magnitude : magnitude;
}

function absoluteValue(value: bigint): bigint {
  return value < 0n ? -value : value;
}
